import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Users,
  BarChart2,
  MessageSquare,
  FolderOpen,
  Repeat,
  Bot,
  Sparkles,
  Target,
  Zap,
  GraduationCap,
  Settings,
  Hash,
  Video,
  FileText,
  Mail,
  Layers,
  Shuffle,
  CreditCard
} from 'lucide-react';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: React.ElementType;
  category: 'page' | 'tool';
  parent?: string;
}

interface SearchDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  searchTerm: string;
  onSelectResult: (result: SearchResult) => void;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({
  isOpen,
  onClose,
  searchTerm,
  onSelectResult
}) => {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // All available pages and tools
  const allItems: SearchResult[] = [
    // Main Pages
    { 
      id: 'dashboard', 
      title: 'Dashboard', 
      description: 'Main dashboard with overview of your content', 
      path: '/dashboard', 
      icon: BarChart2,
      category: 'page'
    },
    { 
      id: 'performance', 
      title: 'Performance', 
      description: 'Analytics and performance metrics', 
      path: '/performance', 
      icon: BarChart2,
      category: 'page'
    },
    { 
      id: 'messaging', 
      title: 'Messaging', 
      description: 'Unified inbox for all platforms', 
      path: '/messaging', 
      icon: MessageSquare,
      category: 'page'
    },
    { 
      id: 'calendar', 
      title: 'Calendar', 
      description: 'Content calendar and scheduling', 
      path: '/calendar', 
      icon: Calendar,
      category: 'page'
    },
    { 
      id: 'contacts', 
      title: 'Contacts', 
      description: 'Manage your network', 
      path: '/contacts', 
      icon: Users,
      category: 'page'
    },
    { 
      id: 'library', 
      title: 'Content Vault', 
      description: 'Digital asset management', 
      path: '/library', 
      icon: FolderOpen,
      category: 'page'
    },
    { 
      id: 'repurpose', 
      title: 'Content Repurposing', 
      description: 'Transform content for different platforms', 
      path: '/repurpose', 
      icon: Repeat,
      category: 'page'
    },
    { 
      id: 'creator-assistant', 
      title: 'Creator Assistant', 
      description: 'AI-powered content assistant', 
      path: '/creator-assistant', 
      icon: Bot,
      category: 'page'
    },
    { 
      id: 'campaign-generator', 
      title: 'Campaign Generator', 
      description: 'Create targeted campaigns', 
      path: '/campaign-generator', 
      icon: Target,
      category: 'page'
    },
    { 
      id: 'learning-hub', 
      title: 'Learning Hub', 
      description: 'Educational resources', 
      path: '/learning-hub', 
      icon: GraduationCap,
      category: 'page'
    },
    { 
      id: 'automated-posts', 
      title: 'Automated Posts', 
      description: 'Schedule and automate content', 
      path: '/automated-posts', 
      icon: Zap,
      category: 'page'
    },
    { 
      id: 'subscription', 
      title: 'Subscription', 
      description: 'Manage your subscription plan', 
      path: '/subscription', 
      icon: CreditCard,
      category: 'page'
    },
    { 
      id: 'settings', 
      title: 'Settings', 
      description: 'Account and platform settings', 
      path: '/settings', 
      icon: Settings,
      category: 'page'
    },
    
    // AI Tools
    { 
      id: 'hashtag-generator', 
      title: 'Hashtag Generator', 
      description: 'Generate optimized hashtags', 
      path: '/ai-tools/hashtag-generator', 
      icon: Hash,
      category: 'tool',
      parent: 'AI Tools'
    },
    { 
      id: 'reel-script-generator', 
      title: 'Reel Script Generator', 
      description: 'Create engaging video scripts', 
      path: '/ai-tools/reel-script-generator', 
      icon: Video,
      category: 'tool',
      parent: 'AI Tools'
    },
    { 
      id: 'seo-caption-analyzer', 
      title: 'SEO Caption Analyzer', 
      description: 'Analyze and improve captions', 
      path: '/ai-tools/seo-caption-analyzer', 
      icon: FileText,
      category: 'tool',
      parent: 'AI Tools'
    },
    { 
      id: 'post-idea-brainstormer', 
      title: 'Post Idea Brainstormer', 
      description: 'Generate content ideas', 
      path: '/ai-tools/post-idea-brainstormer', 
      icon: Sparkles,
      category: 'tool',
      parent: 'AI Tools'
    },
    { 
      id: 'carousel-outline-builder', 
      title: 'Carousel Outline Builder', 
      description: 'Create carousel post outlines', 
      path: '/ai-tools/carousel-outline-builder', 
      icon: Layers,
      category: 'tool',
      parent: 'AI Tools'
    },
    { 
      id: 'email-draft-generator', 
      title: 'Email Draft Generator', 
      description: 'Convert posts to email format', 
      path: '/ai-tools/email-draft-generator', 
      icon: Mail,
      category: 'tool',
      parent: 'AI Tools'
    },
    { 
      id: 'ab-hook-tester', 
      title: 'A/B Hook Tester', 
      description: 'Test different hooks for engagement', 
      path: '/ai-tools/ab-hook-tester', 
      icon: Shuffle,
      category: 'tool',
      parent: 'AI Tools'
    },
    
    // Campaign Tools
    { 
      id: 'follower-campaign', 
      title: 'Follower Campaign', 
      description: 'Grow your audience', 
      path: '/follower-campaign', 
      icon: Users,
      category: 'tool',
      parent: 'Campaign Generator'
    },
    { 
      id: 'product-campaign', 
      title: 'Product Campaign', 
      description: 'Promote your products', 
      path: '/product-campaign', 
      icon: Target,
      category: 'tool',
      parent: 'Campaign Generator'
    },
    { 
      id: 'brand-campaign', 
      title: 'Brand Campaign', 
      description: 'Build your brand presence', 
      path: '/brand-campaign', 
      icon: Sparkles,
      category: 'tool',
      parent: 'Campaign Generator'
    },
    { 
      id: 'community-campaign', 
      title: 'Community Campaign', 
      description: 'Grow your community', 
      path: '/community-campaign', 
      icon: Users,
      category: 'tool',
      parent: 'Campaign Generator'
    },
    { 
      id: 'giveaway-campaign', 
      title: 'Giveaway Campaign', 
      description: 'Run giveaways and contests', 
      path: '/giveaway-campaign', 
      icon: Target,
      category: 'tool',
      parent: 'Campaign Generator'
    }
  ];

  // Filter results based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      // Show recommended tools when no search term
      setResults(allItems.filter(item => item.category === 'tool').slice(0, 6));
      return;
    }
    
    const filtered = allItems.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.title.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        (item.parent && item.parent.toLowerCase().includes(searchLower))
      );
    });
    
    setResults(filtered);
    setSelectedIndex(0);
  }, [searchTerm]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            handleSelectResult(results[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && results.length > 0) {
      const selectedElement = resultsRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, results]);

  const handleSelectResult = (result: SearchResult) => {
    onSelectResult(result);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ type: 'spring', damping: 25 }}
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 z-50 overflow-hidden"
          style={{ maxHeight: '60vh' }}
        >
          <div 
            ref={resultsRef}
            className="max-h-[60vh] overflow-y-auto"
          >
            {results.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                No results found
              </div>
            ) : (
              <div className="py-2">
                {/* Group results by category */}
                {['page', 'tool'].map(category => {
                  const categoryResults = results.filter(r => r.category === category);
                  if (categoryResults.length === 0) return null;
                  
                  return (
                    <div key={category} className="mb-2">
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        {category === 'page' ? 'Pages' : 'Tools'}
                      </div>
                      {categoryResults.map((result, index) => {
                        const resultIndex = results.findIndex(r => r.id === result.id);
                        const Icon = result.icon;
                        return (
                          <div
                            key={result.id}
                            data-index={resultIndex}
                            className={`px-4 py-3 cursor-pointer flex items-start ${
                              selectedIndex === resultIndex
                                ? 'bg-indigo-50 dark:bg-indigo-900/20'
                                : 'hover:bg-gray-50 dark:hover:bg-dark-700'
                            }`}
                            onClick={() => handleSelectResult(result)}
                          >
                            <div className={`p-2 rounded-lg ${
                              selectedIndex === resultIndex
                                ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                                : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                            }`}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="ml-3 flex-1">
                              <div className="flex items-center">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                  {result.title}
                                </h4>
                                {result.parent && (
                                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                    in {result.parent}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {result.description}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Keyboard shortcuts */}
            <div className="px-4 py-3 border-t border-gray-200 dark:border-dark-700 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-700 rounded text-xs">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-700 rounded text-xs ml-1">↓</kbd>
                  <span className="ml-1.5">to navigate</span>
                </div>
                <div className="flex items-center">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-700 rounded text-xs">Enter</kbd>
                  <span className="ml-1.5">to select</span>
                </div>
                <div className="flex items-center">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-700 rounded text-xs">Esc</kbd>
                  <span className="ml-1.5">to close</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchDropdown;